// SSE-Hook (Besitzer: api-kern): eine EventSource je Tab auf /api/core/events, geteilt von allen
// Abonnenten, mit Reconnect (exponentiell bis 30 s) und Wiederaufnahme über die letzte Ereignis-ID.
import type { EventEnvelope } from '@bar/contracts/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { currentAccessToken, personaOverride } from './api/client';
import { coreKeys } from './api/core';

/** Ereignistypen aus SPEC §5.4 „Ereignisbus“ (EventSource braucht je Typ einen Listener). */
export const KNOWN_EVENT_TYPES = [
  'task.created',
  'task.updated',
  'notification.created',
  'scorecard.submitted',
  'scorecard.returned',
  'cycle.phase_changed',
  'cycle.progress',
  'cycle.published',
  'calibration.updated',
  'promotion.updated',
  'promotion.announced',
  'pip.updated',
  'bonus.updated',
  'bonus.announced',
  'contract.amended',
  'contract.activated',
  'contract.probation_ending',
  'contract.ending',
  'contract.ended',
  'goal.updated',
  'chat.message.created',
  'chat.message.updated',
  'chat.message.deleted',
  'chat.reaction.changed',
  'chat.read',
  'presence',
  'kb.index.updated',
  'source.sync.started',
  'source.sync.finished',
  'source.sync.failed',
  'source.share.changed',
  'device.code.approved',
] as const;

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting';

export interface EventSourceLike {
  readyState: number;
  onopen: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
}

type Factory = (url: string) => EventSourceLike;
const CLOSED = 2;
const MAX_DELAY_MS = 30_000;

interface Subscriber {
  handler: (event: EventEnvelope) => void;
  types?: ReadonlySet<string>;
}

export class EventStream {
  private readonly subscribers = new Set<Subscriber>();
  private readonly statusListeners = new Set<() => void>();
  private readonly registered = new Set<string>();
  private source: EventSourceLike | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private attempt = 0;
  private lastId = 0;
  private generation = 0;
  status: StreamStatus = 'idle';

  constructor(
    private factory: Factory = (url) => new EventSource(url, { withCredentials: true }),
    private readonly path = '/api/core/events',
  ) {}

  /** Für Tests: eigene EventSource-Fabrik. */
  setFactory(factory: Factory) {
    this.factory = factory;
  }

  subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);
    for (const type of subscriber.types ?? []) this.listen(type);
    if (this.subscribers.size === 1) void this.connect();
    return () => {
      this.subscribers.delete(subscriber);
      if (this.subscribers.size === 0) this.disconnect();
    };
  }

  onStatus(listener: () => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /** Neu verbinden (z. B. nach Konto-Wechsel); der Wiederaufnahme-Stand wird verworfen. */
  restart() {
    this.lastId = 0;
    if (this.subscribers.size === 0) return;
    this.disconnect();
    void this.connect();
  }

  private setStatus(status: StreamStatus) {
    this.status = status;
    for (const listener of this.statusListeners) listener();
  }

  private async connect() {
    const generation = ++this.generation;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const params = new URLSearchParams();
    if (this.lastId > 0) params.set('lastEventId', String(this.lastId));
    const persona = personaOverride();
    if (persona) params.set('as', persona);
    const token = await currentAccessToken();
    if (token) params.set('access_token', token);
    if (generation !== this.generation) return; // inzwischen getrennt oder neu gestartet
    const qs = params.toString();
    const source = this.factory(qs ? `${this.path}?${qs}` : this.path);
    this.source = source;
    this.registered.clear();
    for (const type of this.allTypes()) this.listen(type);
    source.addEventListener('ready', () => this.opened());
    source.onopen = () => this.opened();
    source.onerror = () => {
      if (source.readyState === CLOSED) this.scheduleReconnect();
      else this.setStatus('reconnecting'); // der Browser verbindet selbst neu
    };
  }

  private opened() {
    this.attempt = 0;
    this.setStatus('open');
  }

  private allTypes(): Set<string> {
    const types = new Set<string>(KNOWN_EVENT_TYPES);
    for (const s of this.subscribers) for (const t of s.types ?? []) types.add(t);
    return types;
  }

  private listen(type: string) {
    if (!this.source || this.registered.has(type)) return;
    this.registered.add(type);
    this.source.addEventListener(type, (event) => this.dispatch(type, event));
  }

  private dispatch(type: string, event: MessageEvent) {
    let envelope: EventEnvelope;
    try {
      envelope = JSON.parse(String(event.data)) as EventEnvelope;
    } catch {
      return;
    }
    const id = Number(event.lastEventId) || envelope.id;
    if (Number.isFinite(id) && id > this.lastId) this.lastId = id;
    for (const s of this.subscribers) {
      if (!s.types || s.types.has(type)) s.handler({ ...envelope, type });
    }
  }

  private scheduleReconnect() {
    this.source?.close();
    this.source = undefined;
    clearTimeout(this.timer);
    const delay =
      Math.min(MAX_DELAY_MS, 1000 * 2 ** this.attempt) + Math.floor(Math.random() * 250);
    this.attempt += 1;
    this.setStatus('reconnecting');
    this.timer = setTimeout(() => void this.connect(), delay);
  }

  private disconnect() {
    this.generation += 1;
    clearTimeout(this.timer);
    this.source?.close();
    this.source = undefined;
    this.attempt = 0;
    this.setStatus('idle');
  }
}

/** Eine Verbindung je Tab. */
export const eventStream = new EventStream();

export interface UseEventsOptions {
  /** Nur diese Typen (Standard: alle). */
  types?: readonly string[];
  enabled?: boolean;
}

/** Ereignisse des Servers abonnieren; der Handler darf sich bei jedem Render ändern. */
export function useEvents(handler: (event: EventEnvelope) => void, options: UseEventsOptions = {}) {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  const enabled = options.enabled ?? true;
  const typesKey = options.types?.join(',') ?? '';
  useEffect(() => {
    if (!enabled) return;
    const types = typesKey ? new Set(typesKey.split(',')) : undefined;
    return eventStream.subscribe({ handler: (event) => ref.current(event), types });
  }, [enabled, typesKey]);
}

export function useEventStreamStatus(): StreamStatus {
  return useSyncExternalStore(
    (listener) => eventStream.onStatus(listener),
    () => eventStream.status,
    () => 'idle',
  );
}

/** Hält Aufgaben- und Benachrichtigungs-Queries live (einmal in der Shell einbinden). */
export function useCoreEventInvalidation(enabled = true) {
  const queryClient = useQueryClient();
  useEvents(
    (event) => {
      if (event.type.startsWith('task.')) {
        void queryClient.invalidateQueries({ queryKey: coreKeys.tasksAll });
        void queryClient.invalidateQueries({ queryKey: coreKeys.taskCounts });
      }
      if (event.type === 'notification.created') {
        void queryClient.invalidateQueries({ queryKey: coreKeys.notificationsAll });
        void queryClient.invalidateQueries({ queryKey: coreKeys.unreadCount });
      }
    },
    { types: ['task.created', 'task.updated', 'notification.created'], enabled },
  );
}
